import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Pagination, Modal, Empty } from "antd";
import moment from "moment";

import { BlocklistAction } from "../../store/actions";

import useScreenSize from "../../utils/usescreensize";

import DashboardCard from "../../components/dashboardcard/dashboardcard";
import Cardloader from "../../components/loader/cardloader";
import Spacer from "../../components/spacer/spacer";
import UnderHeaderMenu from "../../components/underheadermenu";
import { PrimaryButton, WhiteButton } from "../../components/button";
import SortDropdown from "../../components/dropdown/sortdropdown";

import images from "../../assets";
import "./styles.css";

const Blocklist = () => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultPage = parseInt(searchParams.get("page")) || 1;

  const { blocklist, isLoading } = useSelector((state) => state?.blocklist);

  const [state, setState] = useState({
    sortOption: "desc",
  });
  const [pageNo, setPageNo] = useState(defaultPage);
  const [pageSize] = useState(8);
  const [isOpen, setOpen] = useState(false);
  const [username, setUsername] = useState("");

  useEffect(() => {
    fetchBlocklist();
  }, [state.sortOption]);

  useEffect(() => {
    setSearchParams({ page: pageNo.toString() });
  }, [pageNo]);

  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page")) || 1;
    if (urlPage !== pageNo) {
      setPageNo(urlPage);
    }
  }, [searchParams]);

  const fetchBlocklist = () => {
    const payload = {
      sort: state?.sortOption,
    };
    dispatch(BlocklistAction.getBlocklist(payload));
  };

  const sortedBlocklist = useMemo(() => {
    if (!blocklist) return [];
    let sortedData = [...blocklist];

    if (state.sortOption === "desc") {
      sortedData.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else {
      sortedData.sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
    }

    return sortedData;
  }, [blocklist, state.sortOption]);

  const paginatedBlocklist = useMemo(() => {
    return sortedBlocklist.slice((pageNo - 1) * pageSize, pageNo * pageSize);
  }, [sortedBlocklist, pageNo, pageSize]);

  const handleUnblock = () => {
    setOpen(false);
    const payload = { blockedId: username };
    dispatch(
      BlocklistAction.deleteBlocklist(payload, () => {
        fetchBlocklist();
      })
    );
  };

  return (
    <div>
      {screenSize.width < 1023 && (
        <UnderHeaderMenu titlename="My Blocklists">
          <div style={{ width: "110px" }}>
            <SortDropdown
              data={[
                { label: "Newest First", value: "desc" },
                { label: "Oldest First", value: "asc" },
              ]}
              onChange={(e) => setState({ ...state, sortOption: e })}
              value={state?.sortOption || undefined}
              placeholder="Sort by"
            />
          </div>
        </UnderHeaderMenu>
      )}
      <Spacer height="16px" />
      <div className="blocklist-section-top-part-container">
        <div></div>
        {screenSize.width > 1023 && (
          <div className="blocklist-section-header-container">
            <h1 className="blocklist-header">My Blocklists</h1>
          </div>
        )}
        {screenSize.width > 1023 && (
          <div className="blocklist-sort-container">
            <div style={{ width: "150px" }}>
              <SortDropdown
                data={[
                  { label: "Newest First", value: "desc" },
                  { label: "Oldest First", value: "asc" },
                ]}
                onChange={(e) => setState({ ...state, sortOption: e })}
                value={state?.sortOption || undefined}
                placeholder="Sort by"
              />
            </div>
          </div>
        )}
      </div>
      <Spacer height="12px" />
      <div className="blocklist-results-container">
        {isLoading ? (
          <Cardloader />
        ) : (
          <>
            {paginatedBlocklist.length > 0 ? (
              <>
                <div className="blocklist-results">
                  {paginatedBlocklist.map((item, index) => (
                    <DashboardCard
                      blocklist={true}
                      item={item}
                      index={index}
                      key={index}
                      redirectedfrom="blocklist"
                    >
                      <div className="flex flex-col w-full gap-3">
                        <div className="blocklist-card-text">
                          <p>Blocked Since:</p>
                          <p>
                            {moment(item?.updatedAt).format(
                              "YYYY-MM-DD, hh:mm A"
                            )}
                          </p>
                        </div>
                        <PrimaryButton
                          style={{ height: "32px" }}
                          text="Unblock"
                          onClick={() => {
                            setUsername(item?.username);
                            setOpen(true);
                          }}
                        />
                      </div>
                    </DashboardCard>
                  ))}
                </div>
                <Spacer height="36px" />
                <div className="flex justify-center items-center">
                  <Pagination
                    pageSize={pageSize}
                    total={sortedBlocklist.length}
                    current={pageNo}
                    showSizeChanger={false}
                    onChange={(page) => setPageNo(page)}
                  />
                </div>
                <Spacer height="24px" />
              </>
            ) : (
              <div className="flex flex-col justify-center items-center p-10 w-full gap-4">
                <img
                  src={images.noActivityIcon}
                  alt="no notifications"
                  className="h-[160px] lg:h-full"
                />
                <div className="text-basePrimary font-bold text-[24px] lg:text-[32px]">
                  You Haven’t Blocked Anyone
                </div>
                <p className="text-[14px] text-center max-w-[500px]">
                  If you block someone, they’ll appear here.
                </p>
              </div>
            )}
          </>
        )}

        <Modal
          centered={true}
          open={isOpen}
          onCancel={() => {
            setUsername("");
            setOpen(false);
          }}
          footer={false}
          closable={true}
        >
          <div className="gap-4 set-profile-photo-content">
            <div className="set-profile-photo-icon">
              <img
                style={{ maxWidth: "40px" }}
                src={images?.deleteIcon}
                alt=""
              />
            </div>
            <div className="set-profile-photo-heading">
              <h2>Are you sure you want to unblock this user?</h2>
              <p>You will be able to communicate with them again.</p>
            </div>
          </div>
          <Spacer height="16px" />
          <div className="flex w-full justify-end">
            <div className="flex gap-4 set-profile-photo-btn">
              <WhiteButton
                style={{ height: "40px", border: "2px solid #EAE6ED" }}
                text="Cancel"
                onClick={() => {
                  setUsername("");
                  setOpen(false);
                }}
              />
              <PrimaryButton
                text="Unblock"
                style={{ background: "#F31260", height: "40px" }}
                onClick={handleUnblock}
              />
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Blocklist;
